import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../components/UserContext';

// Module-level cache so the dialog, the page, the panels and the bolita share
// one read per org per session instead of each fetching on mount.
const cache = new Map();       // org -> Array<{id, nombre, correo, rol}>
const inflight = new Map();    // org -> Promise

const ROLES = ['owner', 'operator'];

async function cargar(org) {
  if (cache.has(org)) return cache.get(org);
  if (inflight.has(org)) return inflight.get(org);
  const p = (async () => {
    const snap = await getDocs(query(collection(db, 'usuarios'), where('org', '==', org)));
    const list = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((u) => ROLES.includes(u.rol))
      .map((u) => ({ id: u.id, nombre: u.nombre || '', correo: u.correo || '', rol: u.rol }))
      .sort((a, b) => (a.nombre || a.correo).localeCompare(b.nombre || b.correo, 'es'));
    cache.set(org, list);
    inflight.delete(org);
    return list;
  })();
  inflight.set(org, p);
  return p;
}

/**
 * The org's assignable members (owners + operators), read from `usuarios` by
 * org — the same source the scheduler and Notificaciones use, NOT the legacy
 * orgs.usuarios array. One-shot and cached: membership changes are rare and
 * a stale name in a picker is harmless.
 *
 * @return {{miembros: Array<Object>, nombreDe: function(string): string}}
 */
export function useOrgMiembros() {
  const { userData } = useUser();
  const org = userData?.org || null;
  const [miembros, setMiembros] = useState(() => (org && cache.get(org)) || []);

  useEffect(() => {
    if (!org) return undefined;
    let vivo = true;
    cargar(org)
      .then((list) => { if (vivo) setMiembros(list); })
      .catch((e) => console.error('Failed to load org members:', e));
    return () => { vivo = false; };
  }, [org]);

  const porId = useMemo(() => {
    const m = {};
    miembros.forEach((u) => { m[u.id] = u; });
    return m;
  }, [miembros]);

  const nombreDe = (uid) => {
    if (!uid) return '';
    const u = porId[uid];
    return u ? (u.nombre || u.correo || uid) : '';
  };

  return { miembros, porId, nombreDe };
}

export default useOrgMiembros;
